import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { Employee, Role } from "@/hooks/useAdminAuth";

interface EmployeesPanelProps {
  fetchEmployees: (params?: Record<string, unknown>) => Promise<any>;
  createEmployee: (payload: Record<string, unknown>) => Promise<any>;
  updateEmployee: (employeeId: number, payload: Record<string, unknown>) => Promise<any>;
  deleteEmployee: (employeeId: number) => Promise<any>;
  fetchRoles: () => Promise<any>;
  onMessage: (msg: string) => void;
}

function CenterToast({ message, visible, fading }: { message: string; visible: boolean; fading: boolean }) {
  if (!visible || !message) return null;
  const isSuccess = message.includes("成功");
  const isError = message.includes("失败") || message.includes("错误") || message.includes("不可") || message.includes("停用");
  return (
    <div className={`fixed inset-0 z-[200] flex items-center justify-center pointer-events-none transition-opacity duration-400 ${fading ? "opacity-0" : "opacity-100"}`}>
      <div className={`flex items-center gap-2.5 px-6 py-3.5 rounded-2xl shadow-xl text-sm font-medium max-w-sm text-center ${isSuccess ? "bg-emerald-600 text-white" : isError ? "bg-red-500 text-white" : "bg-[#1a3a5c] text-white"}`}>
        <i className={`${isSuccess ? "ri-checkbox-circle-line" : isError ? "ri-error-warning-line" : "ri-information-line"} text-base shrink-0`}></i>
        <span>{message}</span>
      </div>
    </div>
  );
}

export default function EmployeesPanel({
  fetchEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  fetchRoles,
  onMessage,
}: EmployeesPanelProps) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empCount, setEmpCount] = useState(0);
  const [empLoading, setEmpLoading] = useState(false);
  const [empSearch, setEmpSearch] = useState("");
  const [empModalError, setEmpModalError] = useState("");
  const [empPage, setEmpPage] = useState(0);
  const empPageSize = 20;
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [empForm, setEmpForm] = useState({ name: "", phone: "", username: "", password: "", role_id: "", notes: "", status: "active" });
  const [deleteEmpTarget, setDeleteEmpTarget] = useState<Employee | null>(null);
  const [deleteEmpLoading, setDeleteEmpLoading] = useState(false);
  const [editingEmp, setEditingEmp] = useState<Employee | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [advisorFilter, setAdvisorFilter] = useState<"all" | "yes" | "no">("all");

  // Super admin disable confirmation
  const [showSuperAdminDisableConfirm, setShowSuperAdminDisableConfirm] = useState(false);
  const [pendingSuperAdminSave, setPendingSuperAdminSave] = useState(false);

  // Center toast state
  const [toastMsg, setToastMsg] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const [toastFading, setToastFading] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setToastFading(false);
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setToastFading(true);
      toastTimer.current = setTimeout(() => {
        setToastVisible(false);
        setToastMsg("");
        setToastFading(false);
      }, 400);
    }, 2500);
  }, []);

  // Get current logged-in admin username from localStorage
  const currentAdminUsername = useMemo(() => {
    try {
      const stored = localStorage.getItem("admin_user");
      if (stored) return JSON.parse(stored)?.username || "";
    } catch { /* ignore */ }
    return "";
  }, []);

  const loadEmployees = useCallback(async () => {
    setEmpLoading(true);
    const res = await fetchEmployees({ search: empSearch || undefined, limit: empPageSize, offset: empPage * empPageSize });
    setEmpLoading(false);
    if (res?.success) { setEmployees(res.employees || []); setEmpCount(res.count || 0); }
  }, [fetchEmployees, empSearch, empPage]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [employees]);

  const hasAdvisorEmployees = useMemo(() => {
    return sortedEmployees.some((e) => e.roles?.client_sub_permissions?.assign_advisor === true);
  }, [sortedEmployees]);

  const filteredEmployees = useMemo(() => {
    if (advisorFilter === "all") return sortedEmployees;
    return sortedEmployees.filter((e) => {
      const isAdvisor = e.roles?.client_sub_permissions?.assign_advisor === true;
      return advisorFilter === "yes" ? isAdvisor : !isAdvisor;
    });
  }, [sortedEmployees, advisorFilter]);

  const handleSaveEmp = async (skipSuperAdminCheck = false) => {
    if (!empForm.name.trim() || !empForm.username.trim()) { setEmpModalError("姓名和账号为必填项"); return; }
    if (!editingEmp?.is_super_admin && !empForm.role_id) { setEmpModalError("权限角色为必选项"); return; }
    if (!editingEmp && !empForm.password.trim()) { setEmpModalError("新增员工时密码为必填项"); return; }

    // Check: if editing a super admin, about to set status to inactive → show confirmation first
    if (
      !skipSuperAdminCheck &&
      editingEmp?.is_super_admin &&
      empForm.status === "inactive" &&
      editingEmp?.status !== "inactive"
    ) {
      setShowSuperAdminDisableConfirm(true);
      return;
    }

    setEmpLoading(true);
    const isSuperEdit = editingEmp?.is_super_admin;
    const payload: Record<string, unknown> = {
      name: empForm.name.trim(), phone: empForm.phone.trim() || null,
      username: empForm.username.trim(),
      ...(isSuperEdit ? {} : { role_id: empForm.role_id ? Number(empForm.role_id) : null }),
      notes: empForm.notes.trim() || null, status: empForm.status,
    };
    if (empForm.password.trim()) payload.password = empForm.password.trim();
    const res = editingEmp ? await updateEmployee(editingEmp.id, payload) : await createEmployee(payload as any);
    setEmpLoading(false);
    if (res?.success) {
      setShowEmpModal(false); setEditingEmp(null); setEmpModalError(""); loadEmployees();
      showToast(editingEmp ? "员工更新成功" : "员工创建成功");
    } else { setEmpModalError(res?.error || "操作失败"); }
  };

  const handleDeleteClick = (e: Employee) => {
    // Priority 1: deleting own account
    if (e.username === currentAdminUsername) {
      showToast("删除失败！该账号为当前登录账号");
      return;
    }
    // Priority 2: active status
    if (e.status === "active") {
      showToast("在职员工不可删除，请先将员工状态调整为「停用」");
      return;
    }
    // Otherwise: show confirm dialog
    setDeleteEmpTarget(e);
  };

  const handleDeleteEmp = async () => {
    if (!deleteEmpTarget) return;
    setDeleteEmpLoading(true);
    const res = await deleteEmployee(deleteEmpTarget.id);
    setDeleteEmpLoading(false);
    if (res?.success) {
      setDeleteEmpTarget(null); loadEmployees();
      const transferred = res.transferred_clients || 0;
      showToast(transferred > 0 ? `删除成功，已将 ${transferred} 位客户转移至超级管理员` : "删除成功");
    } else { setDeleteEmpTarget(null); showToast(res?.error || "删除失败"); }
  };

  const openEmpModal = async (emp?: Employee) => {
    if (roles.length === 0) {
      const res = await fetchRoles();
      if (res?.success) setRoles(res.roles || []);
    }
    if (emp) {
      setEditingEmp(emp);
      setEmpForm({
        name: emp.name || "",
        phone: emp.phone || "",
        username: emp.username,
        password: "",
        role_id: emp.is_super_admin ? "__super_admin__" : (emp.role_id ? String(emp.role_id) : ""),
        notes: emp.notes || "",
        status: emp.status || "active",
      });
    } else {
      setEditingEmp(null);
      setEmpForm({ name: "", phone: "", username: "", password: "", role_id: "", notes: "", status: "active" });
    }
    setEmpModalError(""); setShowEmpModal(true);
  };

  return (
    <div className="space-y-4">
      <CenterToast message={toastMsg} visible={toastVisible} fading={toastFading} />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[#1a3a5c]">员工列表</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">管理后台登录账号与权限分配</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
            <input type="text" value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setEmpPage(0), loadEmployees())} placeholder="搜索姓名/账号" className="pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] bg-white w-40" />
          </div>
          <button onClick={() => { setEmpPage(0); loadEmployees(); }} className="px-3 py-2 bg-[#1a3a5c] text-white rounded-lg text-xs font-medium hover:bg-[#142d48] transition-colors cursor-pointer whitespace-nowrap"><i className="ri-search-line mr-1"></i>搜索</button>
          {hasAdvisorEmployees && (
            <select value={advisorFilter} onChange={(e) => { setAdvisorFilter(e.target.value as any); setEmpPage(0); }} className="px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
              <option value="all">全部顾问</option>
              <option value="yes">可分配顾问</option>
              <option value="no">不可分配</option>
            </select>
          )}
          {advisorFilter !== "all" && (
            <button onClick={() => { setAdvisorFilter("all"); setEmpPage(0); }} className="px-3 py-2 rounded-lg text-xs text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"><i className="ri-close-line"></i>清除</button>
          )}
          <button onClick={() => openEmpModal()} className="px-3 py-2 bg-[#1a3a5c] text-white rounded-lg text-xs font-medium hover:bg-[#142d48] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"><i className="ri-add-line"></i>新增员工</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">序号</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">姓名</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">账号</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">电话</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">角色</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">状态</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {empLoading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center"><div className="w-8 h-8 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin mx-auto"></div></td></tr>
              ) : filteredEmployees.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm"><i className="ri-inbox-line text-2xl mb-2 block"></i>暂无员工数据</td></tr>
              ) : filteredEmployees.map((e, idx) => (
                <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-500">{empPage * empPageSize + idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-[#1a3a5c]">{e.name}</td>
                  <td className="px-4 py-3">{e.username}</td>
                  <td className="px-4 py-3 text-gray-600">{e.phone || "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#1a3a5c]/10 text-[#1a3a5c]">{e.is_super_admin ? "超级管理员" : e.roles?.name || "未分配"}</span>
                      {e.is_super_admin && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600">全局</span>}
                      {e.roles?.data_scope === "global" && !e.is_super_admin && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600">全局</span>}
                      {e.roles?.data_scope === "personal" && !e.is_super_admin && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">个人</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${e.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>{e.status === "active" ? "在职" : "停用"}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEmpModal(e)} className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-[#1a3a5c]/10 flex items-center justify-center text-gray-400 hover:text-[#1a3a5c] transition-colors cursor-pointer"><i className="ri-edit-line text-xs"></i></button>
                      {!e.is_super_admin && (
                        <button onClick={() => handleDeleteClick(e)} className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer">
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {empCount > empPageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">共 {empCount} 条 · 第 {empPage + 1} / {Math.ceil(empCount / empPageSize)} 页</p>
            <div className="flex gap-2">
              <button onClick={() => setEmpPage((p) => Math.max(0, p - 1))} disabled={empPage === 0} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"><i className="ri-arrow-left-s-line"></i></button>
              <button onClick={() => setEmpPage((p) => p + 1)} disabled={(empPage + 1) * empPageSize >= empCount} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"><i className="ri-arrow-right-s-line"></i></button>
            </div>
          </div>
        )}
      </div>

      {/* Edit / Create Employee Modal */}
      {showEmpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="text-base font-bold text-[#1a3a5c]">{editingEmp ? "编辑员工" : "新增员工"}</h3>
              <button onClick={() => setShowEmpModal(false)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {empModalError && <div className="bg-red-50 text-red-600 text-xs rounded-lg px-3 py-2 flex items-center gap-1.5"><i className="ri-error-warning-line"></i>{empModalError}</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">员工姓名 <span className="text-red-400">*</span></label>
                  <input type="text" value={empForm.name} onChange={(e) => setEmpForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white" placeholder="请输入姓名" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">联系电话</label>
                  <input type="text" value={empForm.phone} onChange={(e) => setEmpForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white" placeholder="请输入电话" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">登录账号 <span className="text-red-400">*</span></label>
                  <input type="text" value={empForm.username} onChange={(e) => setEmpForm((f) => ({ ...f, username: e.target.value }))} disabled={!!editingEmp} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white disabled:bg-gray-50 disabled:text-gray-400" placeholder="请输入登录账号" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">{editingEmp ? "重置密码（留空不修改）" : "密码"} {!editingEmp && <span className="text-red-400">*</span>}</label>
                  <input type="password" value={empForm.password} onChange={(e) => setEmpForm((f) => ({ ...f, password: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white" placeholder={editingEmp ? "留空则不修改" : "请输入密码"} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    权限角色
                    {!editingEmp?.is_super_admin && <span className="text-red-400"> *</span>}
                  </label>
                  {editingEmp?.is_super_admin ? (
                    <div className="w-full px-3 py-2 rounded-lg border border-gray-100 text-sm text-gray-400 bg-gray-50 flex items-center gap-1.5 select-none">
                      <i className="ri-shield-star-line text-[#c9a96e]"></i>超级管理员
                      <span className="text-[10px] text-gray-400 ml-1">(不可修改)</span>
                    </div>
                  ) : (
                    <select value={empForm.role_id} onChange={(e) => setEmpForm((f) => ({ ...f, role_id: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
                      <option value="">请选择角色</option>
                      {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">状态</label>
                  <select value={empForm.status} onChange={(e) => setEmpForm((f) => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
                    <option value="active">在职</option>
                    <option value="inactive">停用</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">备注</label>
                <textarea value={empForm.notes} onChange={(e) => setEmpForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white resize-none" placeholder="请输入备注..." />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowEmpModal(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleSaveEmp(false)} disabled={empLoading} className="px-5 py-2 bg-[#1a3a5c] text-white rounded-lg text-sm font-medium hover:bg-[#142d48] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap">
                {empLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>保存中...</> : "保存"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Super Admin Disable Confirmation */}
      {showSuperAdminDisableConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-shield-keyhole-line text-amber-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-[#1a3a5c] mb-2">停用超级管理员账号</h3>
            <p className="text-xs text-gray-500 mb-5 leading-relaxed">
              请确认存在其他同等管理权限账号，防止停用后无法管理后台。
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowSuperAdminDisableConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setShowSuperAdminDisableConfirm(false);
                  handleSaveEmp(true);
                }}
                disabled={pendingSuperAdminSave}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                确认停用
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteEmpTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-[#1a3a5c] mb-1">确认删除员工？</h3>
            <p className="text-xs text-gray-400 mb-5">
              此操作将永久删除员工「<span className="font-medium text-[#1a3a5c]">{deleteEmpTarget.name}（{deleteEmpTarget.username}）</span>」，
              其名下客户将自动转移至超级管理员，不可恢复。
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleteEmpTarget(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleDeleteEmp} disabled={deleteEmpLoading} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                {deleteEmpLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>删除中...</> : "确认删除"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}